import axiosInstance from "@/hooks/axiosConfig";

export type WelcomeCategory = {
  id?: string;
  name: string;
  count: number;
  description?: string;
  slug?: string;
};

export type WelcomeCategoryListItem = {
  id: string;
  name: string;
  code: string;
  description: string;
  productCount: number;
  slug?: string;
  displayName?: string;
  level?: number;
};

export type WelcomeCategorySort =
  | "name_asc"
  | "name_desc"
  | "newest"
  | "oldest";

export type WelcomeCategoryDirectoryGroup = {
  id: string;
  name: string;
  description: string;
  productCount: number;
  items: WelcomeCategoryListItem[];
  moreCount?: number;
};

export type WelcomeCategoryDirectorySection = {
  id: string;
  name: string;
  code: string;
  slug: string;
  description: string;
  productCount: number;
  groups: WelcomeCategoryDirectoryGroup[];
};

export type WelcomeProduct = {
  id: string;
  name: string;
  image: string;
  price: string;
  unit: string;
  moq: string;
  supplier: string;
  location: string;
  verified: boolean;
  rating: number;
};

export type WelcomeProductSort =
  | "newest"
  | "oldest"
  | "name_asc"
  | "name_desc";

export type WelcomeProductCatalogItem = {
  id: string;
  name: string;
  code: string;
  description: string;
  image: string;
  price: string;
  minPrice: number | null;
  maxPrice: number | null;
  currency: string;
  moq: string;
  supplier: string;
  supplierId: string | null;
  location: string;
  verified: boolean;
  rating: number;
  categoryId: string | null;
  category: string;
  categoryDisplayName: string;
  categorySlug: string;
  hasVariants: boolean;
  skuCount: number;
  createdAt: string | null;
};

export type WelcomeProductRootCategoryFilter = {
  id: string;
  name: string;
  slug: string;
  description: string;
  productCount: number;
};

export type WelcomeProductCategoryFilter = {
  id: string;
  name: string;
  displayName: string;
  slug: string;
  productCount: number;
  level: number;
  isLeaf: boolean;
  rootId: string;
  rootName: string;
};

export type WelcomeProductSupplierFilter = {
  id: string;
  name: string;
  location: string;
  verified: boolean;
  productCount: number;
};

export type WelcomeProductsFilters = {
  rootCategories: WelcomeProductRootCategoryFilter[];
  categories: WelcomeProductCategoryFilter[];
  suppliers: WelcomeProductSupplierFilter[];
};

export type WelcomeProductsPayload = {
  items: WelcomeProductCatalogItem[];
  filters: WelcomeProductsFilters;
  query: string;
  sort: WelcomeProductSort;
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
  appliedFilters: {
    categoryId: string | null;
    sellerProfileId: string | null;
    verified: boolean | null;
    hasVariants: boolean | null;
  };
};

export type WelcomeProductDetailGalleryItem = {
  id: string;
  url: string;
  alt: string;
  skuId: string | null;
};

export type WelcomeProductDetailPathItem = {
  id: string;
  name: string;
};

export type WelcomeProductDetailCategory = {
  id: string;
  name: string;
  displayName: string;
  slug: string;
  path: WelcomeProductDetailPathItem[];
};

export type WelcomeProductDetailVariantAttribute = {
  code: string;
  name: string;
  values: string[];
};

export type WelcomeProductDetailSku = {
  id: string;
  label: string;
  price: string;
  rawPrice: number | null;
  currency: string;
  minOrderQty: number;
  image: string;
  attributes: {
    key: string;
    label: string;
    value: string;
  }[];
};

export type WelcomeProductDetailSpecification = {
  label: string;
  value: string;
};

export type WelcomeProductDetailSupplier = {
  id: string;
  name: string;
  logo: string;
  address: string;
  verified: boolean;
  rating: number;
  responseRate: number;
  website: string;
  email: string;
  phoneNumber: string;
  contactMasked: boolean;
  productCount: number;
  memberSince: string;
  capabilityTags: string[];
  preferredDestinationCountries: string[];
  categories: string[];
};

export type SupplierContactRevealPayload = {
  supplierId: string;
  name: string;
  email: string;
  phoneNumber: string;
  website: string;
  address: string;
  access: {
    alreadyRevealed: boolean;
    used: number;
    limit: number;
    remaining: number | null;
  };
};

export type WelcomeProductDetailsPayload = {
  id: string;
  name: string;
  code: string;
  description: string;
  createdAt: string | null;
  price: string;
  minPrice: number | null;
  maxPrice: number | null;
  currency: string;
  moq: string;
  skuCount: number;
  hasVariants: boolean;
  gallery: WelcomeProductDetailGalleryItem[];
  category: WelcomeProductDetailCategory | null;
  variantAttributes: WelcomeProductDetailVariantAttribute[];
  skus: WelcomeProductDetailSku[];
  specifications: WelcomeProductDetailSpecification[];
  supplier: WelcomeProductDetailSupplier | null;
  relatedProducts: WelcomeProductCatalogItem[];
};

export type WelcomeSupplierSort =
  | "rating_desc"
  | "newest"
  | "oldest"
  | "name_asc"
  | "name_desc";

export type WelcomeSupplierRootCategoryFilter = {
  id: string;
  name: string;
  slug: string;
  description: string;
  supplierCount: number;
};

export type WelcomeSupplierCategoryFilter = {
  id: string;
  name: string;
  displayName: string;
  slug: string;
  supplierCount: number;
  level: number;
  isLeaf: boolean;
  rootId: string;
  rootName: string;
};

export type WelcomeSuppliersFilters = {
  rootCategories: WelcomeSupplierRootCategoryFilter[];
  categories: WelcomeSupplierCategoryFilter[];
};

export type WelcomeSupplierDirectoryItem = {
  id: string;
  name: string;
  logo: string;
  location: string;
  verified: boolean;
  rating: number;
  responseRate: number;
  productCount: number;
  memberSince: string;
  mainProducts: string;
  categories: string[];
  capabilityTags: string[];
  preferredDestinationCountries: string[];
};

export type WelcomeSuppliersPayload = {
  items: WelcomeSupplierDirectoryItem[];
  filters: WelcomeSuppliersFilters;
  query: string;
  sort: WelcomeSupplierSort;
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
  appliedFilters: {
    categoryId: string | null;
    verified: boolean | null;
  };
};

export type WelcomeSupplierDetailsPayload = WelcomeProductDetailSupplier & {
  mainProducts: string;
  products: WelcomeProductCatalogItem[];
};

export type WelcomeBuyingRequest = {
  title: string;
  category: string;
  budget: string;
  location: string;
  timePosted: string;
  quotesReceived: number;
};

export type WelcomeSupplier = {
  id: string;
  name: string;
  logo: string;
  location: string;
  verified: boolean;
  products: number;
  yearEstablished: string;
  mainProducts: string;
};

export type WelcomeDataPayload = {
  categories: WelcomeCategory[];
  products: WelcomeProduct[];
  buyingRequests: WelcomeBuyingRequest[];
  featuredSuppliers: WelcomeSupplier[];
};

export type WelcomeCategoriesPayload = {
  mode: "browse" | "search";
  sections: WelcomeCategoryDirectorySection[];
  query: string;
  sort: WelcomeCategorySort;
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
};

export const fetchWelcomeData = async (): Promise<WelcomeDataPayload> => {
  const response = await axiosInstance.get('/v1/openApi/welcome', {
    headers: {
      'X-Skip-Auth-Refresh': 'true',
    },
  });

  return (
    response.data?.payLoad || {
      categories: [],
      products: [],
      buyingRequests: [],
      featuredSuppliers: [],
    }
  );
};

export const fetchWelcomeCategories = async ({
  query = "",
  cursor = null,
  limit = 24,
  sort = "name_asc",
}: {
  query?: string;
  cursor?: string | null;
  limit?: number;
  sort?: WelcomeCategorySort;
} = {}): Promise<WelcomeCategoriesPayload> => {
  const response = await axiosInstance.get("/v1/openApi/welcome/categories", {
    headers: {
      "X-Skip-Auth-Refresh": "true",
    },
    params: {
      ...(query ? { q: query } : {}),
      ...(cursor ? { cursor } : {}),
      limit,
      sort,
    },
  });

  return (
    response.data?.payLoad || {
      mode: query ? "search" : "browse",
      sections: [],
      query: "",
      sort,
      limit,
      hasMore: false,
      nextCursor: null,
    }
  );
};

export const fetchWelcomeProducts = async ({
  query = "",
  cursor = null,
  limit = 24,
  sort = "newest",
  categoryId = null,
  sellerProfileId = null,
  verified = null,
  hasVariants = null,
}: {
  query?: string;
  cursor?: string | null;
  limit?: number;
  sort?: WelcomeProductSort;
  categoryId?: string | null;
  sellerProfileId?: string | null;
  verified?: boolean | null;
  hasVariants?: boolean | null;
} = {}): Promise<WelcomeProductsPayload> => {
  const response = await axiosInstance.get("/v1/openApi/welcome/products", {
    headers: {
      "X-Skip-Auth-Refresh": "true",
    },
    params: {
      ...(query ? { q: query } : {}),
      ...(cursor ? { cursor } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(sellerProfileId ? { sellerProfileId } : {}),
      ...(verified !== null ? { verified } : {}),
      ...(hasVariants !== null ? { hasVariants } : {}),
      limit,
      sort,
    },
  });

  return (
    response.data?.payLoad || {
      items: [],
      filters: {
        rootCategories: [],
        categories: [],
        suppliers: [],
      },
      query: "",
      sort,
      limit,
      hasMore: false,
      nextCursor: null,
      appliedFilters: {
        categoryId,
        sellerProfileId,
        verified,
        hasVariants,
      },
    }
  );
};

export const fetchWelcomeSuppliers = async ({
  query = "",
  cursor = null,
  limit = 18,
  sort = "rating_desc",
  categoryId = null,
  verified = null,
}: {
  query?: string;
  cursor?: string | null;
  limit?: number;
  sort?: WelcomeSupplierSort;
  categoryId?: string | null;
  verified?: boolean | null;
} = {}): Promise<WelcomeSuppliersPayload> => {
  const response = await axiosInstance.get("/v1/openApi/welcome/suppliers", {
    headers: {
      "X-Skip-Auth-Refresh": "true",
    },
    params: {
      ...(query ? { q: query } : {}),
      ...(cursor ? { cursor } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(verified !== null ? { verified } : {}),
      limit,
      sort,
    },
  });

  return (
    response.data?.payLoad || {
      items: [],
      filters: {
        rootCategories: [],
        categories: [],
      },
      query: "",
      sort,
      limit,
      hasMore: false,
      nextCursor: null,
      appliedFilters: {
        categoryId,
        verified,
      },
    }
  );
};

export const fetchWelcomeProductDetails = async (
  productId: string
): Promise<WelcomeProductDetailsPayload | null> => {
  if (!productId) return null;

  const response = await axiosInstance.get(`/v1/openApi/welcome/products/${productId}`, {
    headers: {
      "X-Skip-Auth-Refresh": "true",
    },
  });

  return response.data?.payLoad || null;
};

export const fetchWelcomeSupplierDetails = async (
  supplierId: string
): Promise<WelcomeSupplierDetailsPayload | null> => {
  if (!supplierId) return null;

  const response = await axiosInstance.get(`/v1/openApi/welcome/suppliers/${supplierId}`, {
    headers: {
      "X-Skip-Auth-Refresh": "true",
    },
  });

  return response.data?.payLoad || null;
};

export const revealSupplierContact = async (
  supplierId: string
): Promise<SupplierContactRevealPayload> => {
  const response = await axiosInstance.get(`/v1/subscriptions/supplier-contacts/${supplierId}`);
  return response.data?.payLoad;
};
